import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon, CircleNotchIcon } from "@phosphor-icons/react";

export default function Button({
    component,
    href,
    to,
    children,
    onClick,
    target,
    submit,
    className = "",
    status = "idle", // idle, pending, success, error
    disabled = false,
    autoResetStatus = true,
    autoResetDelay = 3000
}) {

    const [internalStatus, setInternalStatus] = useState(status);

    useEffect(() => {
        setInternalStatus(status);

        // Auto-reset status after success or error
        if (autoResetStatus && (status === "success" || status === "error")) {
            const timer = setTimeout(() => {
                setInternalStatus("idle");
            }, autoResetDelay);

            return () => clearTimeout(timer);
        }
    }, [status, autoResetStatus, autoResetDelay]);

    let classes = typeof className === "string" ? className.split(" ").reduce((acc, item) => {
        acc[item] = true
        return acc
    }, {}) : className;


    const isPending = internalStatus === "pending";
    const isDisabled = disabled || isPending;

    const Classes = classNames({
        'font-medium flex justify-center items-center gap-2 cursor-pointer px-4 py-3 text-sm transition-all duration-300': true,
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2': true,
        'opacity-50 cursor-not-allowed': disabled && !isPending,
        // Idle state: use custom colors if provided, otherwise default black
        // 'bg-black': !hasCustomBg && internalStatus === "idle",
        // 'text-white': !hasCustomText && internalStatus === "idle",
        // 'hover:bg-greydark': !hasCustomHover && !hasCustomBg && internalStatus === "idle",
        // Pending state: always use default styles
        'bg-black/70 cursor-wait': internalStatus === "pending",
        // Success state: always use green
        'bg-green-600 hover:bg-green-700': internalStatus === "success",
        // Error state: always use red
        'bg-red-600 hover:bg-red-700': internalStatus === "error",
        'text-white': ["success", "error", "pending"].includes(internalStatus),
        ...classes
    });

    let Component = component || 'button';

    if(to && !component) {
        Component = Link;
    }

    // Only a native <button> accepts `type` / `disabled`; forwarding them to a
    // Link or custom element produces invalid markup.
    const isNativeButton = Component === 'button';
    const nativeButtonProps = isNativeButton
        ? { type: submit ? 'submit' : 'button', disabled: isDisabled, 'aria-busy': isPending || undefined }
        : {};

    const renderStatusIcon = () => {
        switch (internalStatus) {
            case "pending":
                return <CircleNotchIcon size={20} className="animate-spin" />;
            case "success":
                return <CheckCircleIcon size={20} weight="fill" />;
            case "error":
                return <XCircleIcon size={20} weight="fill" />;
            default:
                return null;
        }
    };

    const renderContent = () => {
        switch (internalStatus) {
            case "pending":
                return "Caricamento...";
            case "success":
                return "Completato!";
            case "error":
                return "Errore";
            default:
                return children;
        }
    };

    return (
        <Component
            className={Classes}
            href={href}
            target={target}
            onClick={isDisabled ? undefined : onClick}
            to={to}
            {...nativeButtonProps}
        >
            {renderStatusIcon()}
            {renderContent()}
        </Component>
    )
}
